import { TEMPLATE_PRIMARY_FIXED, TEMPLATE_PRIMARY_HYBRID } from 'app/policies/actions/programs.access';
import { setMetadataArraySelect, setMetadataSelect } from 'app/services/metadata.service';
import { getTemplate } from 'app/services/programs.service';
import i18next from 'app/translations/i18n';
import { getCustomTypographyDefault } from 'app/utils/customTypography';
import { MAX, MIN, REQUIRED } from 'app/utils/yupMessage';
import * as Yup from 'yup';

export const TEMPLATE_VANILLA = 'vanilla';
const TEMPLATES = getTemplate();
const VANILLA_TEMPLATE = TEMPLATES.find((item) => item.template_type === TEMPLATE_VANILLA);

export const MODEL_TEXT = 'program';

export const Model = {
  academic_session: [],
  background: '',
  background_mint: '',
  body_typeface_guid: '',
  book_guid: '',
  book_name: '',
  book_thumbnail: '',
  colour_mint: '',
  custom_heading_typeface_guid: '',
  custom_body_typeface_guid: '',
  custom_color_first: '',
  custom_color_second: '',
  custom_color_gradient_1: '',
  custom_color_gradient_2: '',
  custom_color_second_style: '',
  custom_border_radious: null,
  config: '',
  country: '',
  description: '',
  discipline: '',
  educationLevels: '',
  educationYears: '',
  evaluation_period: undefined,
  has_home: false,
  has_power_lesson: false,
  is_powerlesson_limited: false,
  has_tangerine_ai: false,
  heading_typeface_guid: '',
  is_accessible: false,
  isbn_student: '',
  isbn_teacher: '',
  langs: [],
  name: '',
  picture: '',
  program_cover: '',
  program_type: '',
  publication_guid: '',
  structure_levels: 2,
  subtitle: '',
  template: { label: VANILLA_TEMPLATE?.description, value: VANILLA_TEMPLATE?.template_type, ...VANILLA_TEMPLATE },
  templateConfig: '{}',
  training_id: null,
};

export const ModelYup = Yup.object().shape({
  academic_session: Yup.array().notRequired(),
  discipline: Yup.object().required(REQUIRED),
  educationLevels: Yup.object().required(REQUIRED),
  educationYears: Yup.object().required(REQUIRED),
  evaluation_period: Yup.string().when('template', {
    is: (template) => template?.value === TEMPLATE_PRIMARY_FIXED || template?.value === TEMPLATE_PRIMARY_HYBRID,
    then: (schema) => schema.required(REQUIRED),
    otherwise: (schema) => schema.notRequired(),
  }),
  isbn_student: Yup.string().max(80, MAX),
  isbn_teacher: Yup.string().max(80, MAX),
  langs: Yup.array(),
  name: Yup.string().min(3, MIN).max(250, MAX).required(REQUIRED),
  picture: Yup.string(),
  program_cover: Yup.string(),
  publication_guid: Yup.string().required(REQUIRED),
  custom_heading_typeface_guid: Yup.string(),
  custom_body_typeface_guid: Yup.string(),
  custom_color_first: Yup.string(),
  custom_color_second: Yup.string(),
  custom_color_gradient_1: Yup.string(),
  custom_color_gradient_2: Yup.string(),
  custom_color_second_style: Yup.string(),
  custom_border_radious: Yup.number().nullable(),
});

export const ModelYupProgrammingAdvanced = Yup.object().shape({
  academic_session: Yup.array().notRequired(),
  discipline: Yup.object().required(REQUIRED),
  educationLevels: Yup.object().required(REQUIRED),
  educationYears: Yup.object().required(REQUIRED),
  evaluation_period: Yup.string().required(REQUIRED),
  isbn_student: Yup.string().max(80, MAX),
  isbn_teacher: Yup.string().max(80, MAX),
  langs: Yup.array(),
  name: Yup.string().min(3, MIN).max(250, MAX).required(REQUIRED),
  picture: Yup.string(),
  program_cover: Yup.string(),
  publication_guid: Yup.string().required(REQUIRED),
});

export const ModelYupPrimaryHybrid = Yup.object().shape({
  academic_session: Yup.array().notRequired(),
  discipline: Yup.object().required(REQUIRED),
  educationLevels: Yup.object().required(REQUIRED),
  educationYears: Yup.object().required(REQUIRED),
  evaluation_period: Yup.string().required(REQUIRED),
  isbn_student: Yup.string().max(80, MAX),
  isbn_teacher: Yup.string().max(80, MAX),
  langs: Yup.array(),
  name: Yup.string().min(3, MIN).max(250, MAX).required(REQUIRED),
  picture: Yup.string(),
  program_cover: Yup.string(),
  publication_guid: Yup.string().required(REQUIRED),
  custom_heading_typeface_guid: Yup.string(),
  custom_body_typeface_guid: Yup.string(),
  custom_color_first: Yup.string(),
  custom_color_second: Yup.string(),
  custom_color_gradient_1: Yup.string(),
  custom_color_gradient_2: Yup.string(),
  custom_color_second_style: Yup.string(),
  custom_border_radious: Yup.number().nullable(),
});

export const ModelErrors = [{ errno: 1062, message: i18next.t('digitalPrograms:error_isbn_exist') }];

export const ModelSuccess = [
  { id: 'create', message: i18next.t('digitalPrograms:toast_create') },
  { id: 'update', message: i18next.t('digitalPrograms:toast_update') },
];

function sanitizeAcademicSession(data) {
  if (!Array.isArray(data?.academic_sessions)) return Model.academic_session;
  return data.academic_sessions.map((item) => ({ label: item.name, value: item.id }));
}

function sanitizeTemplate(data) {
  if (data.template?.template_type) {
    return setMetadataSelect(data.template, 'description', 'template_type');
  }
  return Model.template;
}

export function sanitizeData(data) {
  const { heading_typeface_default, body_typeface_default } = getCustomTypographyDefault();

  return {
    academic_session: sanitizeAcademicSession(data),
    background: data.background || '',
    background_mint: data.background_mint || '',
    body_typeface_guid: data.body_typeface_guid,
    book_guid: data.book_guid,
    book_name: data.book_name,
    book_thumbnail: data.book_thumbnail,
    colour_mint: data.colour_mint,
    config: data.config || Model.config,
    country: data.country_guid ? setMetadataSelect(data, 'country_name', 'country_guid') : Model.country,
    description: data.description || '',
    discipline: data.education_discipline_guid ? setMetadataSelect(data, 'discipline_name', 'discipline_guid') : Model.discipline,
    educationLevels: data.education_level_guid ? setMetadataSelect(data, 'education_level_name', 'education_level_guid') : Model.educationLevels,
    educationYears: data.education_year_guid ? setMetadataSelect(data, 'education_year_name', 'education_year_guid') : Model.educationYears,
    evaluation_period: data.evaluation_period || Model.evaluation_period,
    guid: data.guid,
    has_home: !!data.has_home,
    has_power_lesson: !!data.has_power_lesson,
    is_powerlesson_limited: !!data.is_powerlesson_limited,
    has_tangerine_ai: !!data.has_tangerine_ai,
    heading_typeface_guid: data.heading_typeface_guid,
    is_accessible: !!data.is_accessible,
    isbn_student: data.isbn_student || '',
    isbn_teacher: data.isbn_teacher || '',
    langs: data.langs ? setMetadataArraySelect(data.langs, 'description', 'id') : Model.langs,
    name: data.name || Model.name,
    pdf_ia_ingested_at: data.pdf_ia_ingested_at,
    pdf_pages_enabled: data.pdf_pages_enabled,
    picture: data.picture || '',
    program_cover: data.program_cover || '',
    program_type: data.program_type ?? '',
    publication_guid: data.publication_guid || Model.publication_guid,
    structure_levels: data?.structure_levels || Model.structure_levels,
    subtitle: data.subtitle || '',
    template: sanitizeTemplate(data),
    templateConfig: data.template ? data.template.json_config : Model.templateConfig,
    training_id: data.training_id ?? null,
    custom_heading_typeface_guid: data.custom_heading_typeface_guid || heading_typeface_default || Model.custom_heading_typeface_guid,
    custom_body_typeface_guid: data.custom_body_typeface_guid || body_typeface_default || Model.custom_body_typeface_guid,
    custom_color_first: data.custom_color_first || '',
    custom_color_second: data.custom_color_second || '',
    custom_color_gradient_1: data.custom_color_gradient_1 || '',
    custom_color_gradient_2: data.custom_color_gradient_2 || '',
    custom_color_second_style: data.custom_color_second_style || '',
    custom_border_radious: data.custom_border_radious ?? null,
  };
}
